import React from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton, LinkIconButton, type LinkIconButtonProps } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import DepartmentIcon from '@atlaskit/icon/core/department';
import EmailIcon from '@atlaskit/icon/core/email';
import LocationIcon from '@atlaskit/icon/core/location';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import TeamsAvatar from '@atlaskit/teams-avatar';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../ai-answer-dialog/ai-answer-context';
import { SainAction, useAnalytics, useSAINUsageCriteriaMet } from '../common/controller/analytics';
import { messages } from '../messages';

import { onKnowledgeCardLinkClick } from './analytics';
import { KnowledgeCardsTopics } from './knowledge-cards-topics';
import { KnowledgeCardsPicture } from './styled';
import { KnowledgeCardsIntentType } from './types';
import { onEmailCopy } from './util';

type KnowledgeCardsHeaderProps = {
	type: KnowledgeCardsIntentType;
	name: string;
	accountId?: string;
	email?: string | null;
	avatarUrl?: string | null;
	profileUrl?: string | null;
	jobTitle?: string | null;
	department?: string | null;
	location?: string | null;
	time?: string | null;
};

const KnowledgeCardsPersonHeaderWrapper = xcss({
	padding: 'space.250',
});

const KnowledgeCardsTeamHeaderWrapper = xcss({
	paddingTop: 'space.150',
	paddingLeft: 'space.250',
	paddingRight: 'space.250',
	paddingBottom: 'space.150',
});

const KnowledgeCardsPrimaryInfoWrapper = xcss({
	display: 'flex',
	width: '100%',
	height: '100%',
	flexDirection: 'column',
	justifyContent: 'center',
});

const KnowledgeCardsButtonsWrapper = xcss({
	paddingTop: 'space.100',
});

export const KnowledgeCardsHeader = ({
	type,
	name,
	accountId,
	email,
	avatarUrl,
	profileUrl,
	jobTitle,
	department,
	location,
	time,
}: KnowledgeCardsHeaderProps) => {
	const { commonAttributes } = useAIAnswerContext();
	const { formatMessage } = useIntl();
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const isPerson = type === KnowledgeCardsIntentType.PERSON;
	const subText = isPerson
		? [jobTitle, department].filter((text) => !!text).join(', ')
		: formatMessage(messages.knowledge_cards_team_subheader);
	const trimmedSubText = subText.length > 250 ? `${subText.slice(0, 250)}...` : subText;

	const timeAndLocationComponent = isPerson && (location || time) && (
		<Text size="medium" color="color.text.subtlest">
			<Inline space="space.100" alignBlock="center">
				<LocationIcon label="" />
				<Inline space="space.050">
					{location && <Text>{location}</Text>}
					{time && location && <Text>•</Text>}
					{time && <Text>{time}</Text>}
				</Inline>
			</Inline>
		</Text>
	);

	const tooltipOptions: LinkIconButtonProps['tooltip'] = {
		position: 'bottom',
		content: formatMessage(messages.knowledge_cards_copy_email_address),
		hideTooltipOnClick: true,
	};

	const { cloudId, sessionId } = commonAttributes;
	const showAITopics = isPerson && fg('kd_topics_in_person_card');

	const viewText = isPerson
		? messages.ai_answer_who_questions_view_profile
		: messages.knowledge_cards_view_team;

	return (
		<Inline
			space="space.100"
			xcss={isPerson ? KnowledgeCardsPersonHeaderWrapper : KnowledgeCardsTeamHeaderWrapper}
		>
			<Inline spread="space-between" alignInline="start" alignBlock="center" grow="fill">
				<Inline space="space.200" alignBlock="center">
					{!isPerson && fg('team-avatar-search-card') ? (
						<TeamsAvatar size="xlarge" src={avatarUrl || undefined} name={name} />
					) : (
						<KnowledgeCardsPicture src={avatarUrl || ''} alt={name} />
					)}
					<Box xcss={KnowledgeCardsPrimaryInfoWrapper}>
						<Stack space="space.100">
							<Heading size="medium">{name}</Heading>
							{trimmedSubText && (
								<Text size="medium" color="color.text.subtlest">
									<Inline space="space.100" alignBlock="center">
										{isPerson && <DepartmentIcon label="" />} <Text>{trimmedSubText}</Text>
									</Inline>
								</Text>
							)}
							{timeAndLocationComponent}
							{showAITopics && (
								<KnowledgeCardsTopics
									cloudId={cloudId}
									userId={accountId ?? ''}
									userName={name}
									sessionId={sessionId}
									fallbackSection={<></>}
									v2styles={true}
								/>
							)}
						</Stack>
					</Box>
				</Inline>
			</Inline>
			<Inline space="space.050" xcss={KnowledgeCardsButtonsWrapper}>
				{!!email && (
					<IconButton
						icon={(props) => <EmailIcon {...props} color={token('color.icon')} />}
						label={formatMessage(messages.ai_answer_who_questions_hover_email_icon)}
						onClick={() => {
							fireAnalyticsEvent(onKnowledgeCardLinkClick(commonAttributes, 'header-copy-email'));
							fireSAINUsageEvent({ metCriteria: SainAction.COPY_CLICKED });
							onEmailCopy(email);
						}}
						isTooltipDisabled={false}
						tooltip={tooltipOptions}
					/>
				)}
				<LinkIconButton
					icon={ArrowRightIcon}
					label={formatMessage(viewText)}
					href={profileUrl || ''}
					onClick={() => {
						fireAnalyticsEvent(onKnowledgeCardLinkClick(commonAttributes, 'header-view-profile'));
						fireSAINUsageEvent({ metCriteria: SainAction.VIEW_PROFILE_CLICKED });
					}}
				/>
			</Inline>
		</Inline>
	);
};
